import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useGetTransportationTypes } from "../../../../../../api/static-data";
import { MEASUREMENT_TYPES } from "../../../../../../config/enums/MeasurementTypes";
import { hasValidationErrors } from "../../../../../../utils/utils";
import {
  InternalTransportFormValues,
  InternalTransportOriginalSnapshot,
  InternalTransportToolVariant,
  TransportTypeOption
} from "../internalTransport.types";
import { mapTransportationTypesToOptions, mergeRetiredTransportType } from "../internalTransport.utils";

interface UseInternalTransportFormOptions {
  data: any;
  status: string;
  variant: InternalTransportToolVariant;
  originalSnapshot: InternalTransportOriginalSnapshot | null;
  lockStatus: boolean;
  isDisconnected: boolean;
}

export const useInternalTransportForm = ({
  data,
  status,
  variant,
  originalSnapshot,
  lockStatus,
  isDisconnected
}: UseInternalTransportFormOptions) => {
  const {
    data: transportationTypesData,
    isSuccess: transportationTypesSuccess,
    isError: transportationTypesError,
    isFetching: transportationTypesFetching,
    refetch: refetchTransportationTypes
  } = useGetTransportationTypes();

  const existingTransportName = useMemo(() => {
    if (status === "success") {
      return data?.internalTransportName ?? "";
    }
    return "";
  }, [status, data?.internalTransportName]);

  const transportationTypes = useMemo((): TransportTypeOption[] | undefined => {
    if (!transportationTypesSuccess) {
      return undefined;
    }
    const options = mapTransportationTypesToOptions(transportationTypesData);
    return mergeRetiredTransportType(options, data?.transportationType);
  }, [transportationTypesData, transportationTypesSuccess, data?.transportationType]);

  const formValues = useMemo<InternalTransportFormValues>(() => {
    const resolvedName =
      variant === "simulate" && originalSnapshot?.transportName
        ? originalSnapshot.transportName
        : data?.internalTransportName || "";

    return {
      internalTransportName: resolvedName,
      averageDistance: data?.averageDistance || 0,
      weight: data?.weight,
      transportationType: data?.transportationType?.toString() || "1",
      weightUnit: data?.weightUnit || MEASUREMENT_TYPES.WEIGHT_KG
    };
  }, [data, originalSnapshot?.transportName, variant]);

  const form = useForm<InternalTransportFormValues>({
    defaultValues: formValues,
    values: formValues
  });

  const isFormDisabled = lockStatus || isDisconnected;
  const saveDisabled =
    hasValidationErrors(form.formState.errors) ||
    lockStatus ||
    isDisconnected ||
    transportationTypesError;

  return {
    ...form,
    form,
    existingTransportName,
    transportationTypes,
    transportationTypesFetching,
    transportationTypesError,
    refetchTransportationTypes,
    isFormDisabled,
    saveDisabled
  };
};
